import logo from "../../../assets/images/logo/Trellis_Logomark_color.png";
import TermsOfService from "../../../components/custom/instructions/TermsOfService";
import AvailableProfilesModal from "../../../modules/merchandising/components/AvailableProfilesModal";
import { Suspense, useEffect, useRef, useState } from "react";
import {
  Navigate,
  Outlet,
  createFileRoute,
  redirect,
  useLocation,
  useMatches,
  useRouter,
} from "@tanstack/react-router";
import { store } from "../../../redux/store";
import Footer from "../../../layouts/Footer";
import Loading from "../../../components/core/blocks/Loading";
import NavBar from "../../../layouts/NavigationBar";
import Sidebar from "../../../layouts/Sidebar";
import { setChannel } from "../../../redux/reducers/channel";
import BeamerInAppNotifications from "../../../components/custom/beamer";
import { selectApplications } from "../../../redux/selectors/applications";
import { ChromeAccessToken } from "../../../components/custom/ChromeAccessToken";
import SpApiTokenExpirationBanner from "../../../components/custom/SpApiTokenExpirationBanner";
import InvalidAmazonPaymentMethodBanner from "../../../components/custom/InvalidAmazonPaymentMethodBanner";
import { LostAmazonAdvertisingProfileAccessBanner } from "../../../components/custom/LostAmazonAdvertisingProfileAccessBanner";
import ChromeExtensionBanner from "../../../components/custom/ChromeExtensionBanner";
import { useAppSelector } from "../../../redux/hooks";
import { useTranslation } from "react-i18next";
import {
  amazonOnboardingStatusOptions,
  useAmazonOnboardingStatus,
} from "../../../modules/onboarding/amazon/useAmazonOnboardingStatus";

/** Where a `/user` URL goes when it matches nothing, or belongs to an application the user doesn't have. */
function useDefaultUserPath() {
  const applications = useAppSelector(selectApplications);
  if (applications.includes("Search Analytics") && !applications.includes("Advertising")) {
    return "/user/searchanalytics";
  }
  return "/user/dashboard";
}

function DefaultUserRedirect() {
  const to = useDefaultUserPath();
  const pathname = useLocation({ select: (location) => location.pathname });
  // Already there but still not allowed in (e.g. Search Analytics-only users): render nothing rather than loop.
  if (pathname === to) return null;
  return <Navigate to={to} replace />;
}

/** Renders the matched `/user` route only when it belongs to one of the user's applications. */
function ApplicationGate() {
  const applications = useAppSelector(selectApplications);
  const application = useMatches({
    select: (matches) => matches.findLast((match) => match.staticData.application)?.staticData.application,
  });

  if (application && !applications.includes(application)) {
    return <DefaultUserRedirect />;
  }
  return <Outlet />;
}

function UserLayout() {
  // Mirrors the onboarding status into redux, where this layout and the pages below read it.
  useAmazonOnboardingStatus();
  const router = useRouter();
  const mainPanel = useRef<HTMLDivElement>(null);
  const { t } = useTranslation(["common"]);

  const authLoading = useAppSelector((state) => state.auth.loading);
  const amz_onboard = useAppSelector((state) => state.amz_onboard);
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const user = useAppSelector((state) => state.user);
  const [showAvailableProfilesModal] = useState(() => {
    const show = sessionStorage.getItem("showProfilesModal") === "true";
    if (show) sessionStorage.removeItem("showProfilesModal");
    return show;
  });

  useEffect(() => {
    if (document.documentElement.className.includes("nav-open")) {
      document.documentElement.classList.toggle("nav-open");
    }
    document.body.classList.add("sidebar-mini");
  }, []);

  useEffect(() => {
    if (amz_onboard.amazon_advertising_access === "connected") {
      if (localStorage.secondaryChannel === "amazon" && localStorage.channel === "walmart") {
        store.dispatch(setChannel("amazon"));
        localStorage.channel = "amazon";
      }
    }
  }, [amz_onboard.amazon_advertising_access]);

  useEffect(() => {
    const isDataLoading =
      authLoading ||
      marketPlace.marketPlace === "None" ||
      (amz_onboard.listings_progress !== "complete" && amz_onboard.listings_progress !== "not applicable") ||
      (amz_onboard.category_progress !== "complete" && amz_onboard.category_progress !== "not applicable") ||
      (amz_onboard.stock_progress !== "complete" && amz_onboard.stock_progress !== "not applicable");

    if (isDataLoading) return;

    const unlisten = router.history.subscribe(({ action }) => {
      if (action.type === "PUSH") {
        document.documentElement.scrollTop = 0;
        if (document.scrollingElement) document.scrollingElement.scrollTop = 0;
        if (mainPanel.current) {
          mainPanel.current.scrollTop = 0;
        }

        if (window.innerWidth < 993 && document.documentElement.className.includes("nav-open")) {
          document.documentElement.classList.toggle("nav-open");
        }
      }
    });

    return unlisten;
  }, [
    router,
    authLoading,
    marketPlace.marketPlace,
    amz_onboard.listings_progress,
    amz_onboard.category_progress,
    amz_onboard.stock_progress,
  ]);

  const { amazon_advertising_access } = amz_onboard;

  return (
    <>
      {authLoading || marketPlace.marketPlace === "None" ?
        <div className="wrap" style={{ height: "100vh" }}>
          <img src={logo} alt={t(($) => $.common["Loading..."])} className="rotate" width="80" height="auto" />
        </div>
      : <div className="wrapper">
          <BeamerInAppNotifications />
          <Sidebar />
          <div className="main-panel" ref={mainPanel}>
            <NavBar />
            <ChromeExtensionBanner />
            <SpApiTokenExpirationBanner />
            {amazon_advertising_access === "connected" && <InvalidAmazonPaymentMethodBanner />}
            <LostAmazonAdvertisingProfileAccessBanner />
            <ChromeAccessToken />

            <Suspense fallback={<Loading fullPage={false} height={300} />}>
              <ApplicationGate />
            </Suspense>

            <Footer fluid />
          </div>
          {user.agreementStatus === "not_agreed" ?
            <TermsOfService />
          : null}
          {showAvailableProfilesModal && <AvailableProfilesModal />}
        </div>
      }
    </>
  );
}

export const Route = createFileRoute("/_authenticated/user")({
  // Users who haven't finished onboarding go back to it.
  beforeLoad: async ({ context }) => {
    const { queryClient } = context;
    // This runs on every navigation under /user, search-only ones included, so it must not wait on the network
    // for a status it already has: a cached one is used as is and refreshed in the background once stale. Only a
    // missing or invalidated status (e.g. right after "Connect Later") is awaited.
    const cached = queryClient.getQueryState(amazonOnboardingStatusOptions.queryKey);
    const statusRequest =
      cached?.data !== undefined && !cached.isInvalidated ?
        queryClient.ensureQueryData({ ...amazonOnboardingStatusOptions, revalidateIfStale: true })
      : queryClient.fetchQuery(amazonOnboardingStatusOptions);
    // A failed status check lets the user through rather than trapping them in onboarding.
    const onboardingStatus = await statusRequest.catch(() => null);
    // Allow access if org_status indicates onboarding was skipped (e.g., "pre_trial", "active")
    if (!onboardingStatus || (onboardingStatus.org_status !== "" && onboardingStatus.org_status !== "onboarding")) {
      return;
    }

    const { user, channel } = context.store.getState();
    const { customer_type, total_monthly_sales } = user;
    if (!customer_type || (customer_type !== "other" && !total_monthly_sales)) {
      throw redirect({ to: "/onboarding/v2/about/type", replace: true });
    }

    if (onboardingStatus.amazon_advertising_access !== "connected") {
      const currentChannel = channel.currentChannel.toLowerCase();
      if (!currentChannel || currentChannel === "none") {
        throw redirect({ to: "/onboarding/v2/channel", replace: true });
      }
      throw redirect({ to: "/onboarding/v2/connect", search: { channel: currentChannel }, replace: true });
    }
  },
  pendingComponent: () => <Loading height="100vh" />,
  component: UserLayout,
  notFoundComponent: DefaultUserRedirect,
});
